import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SiteProvider } from "./context/SiteContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import EmailUnsubscribe from "./pages/EmailUnsubscribe";
import Sales from "./pages/Sales";
import FamilyDeals from "./pages/FamilyDeals";
import FamilyProfile from "./pages/FamilyProfile";
import Alerts from "./pages/Alerts";
import MyUptoo from "./pages/MyUptoo";
import LovedDeals from "./pages/LovedDeals";
import PostDeal from "./pages/PostDeal";
import MerchantStats from "./pages/MerchantStats";
import Upgrade from "./pages/Upgrade";
import { PaymentSuccess, PaymentCancel } from "./pages/Payment";
import Admin from "./pages/Admin";
import ReviewDeal from "./pages/admin/ReviewDeal";
import Marketplace from "./pages/Marketplace";
import LatestDeals from "./pages/LatestDeals";
import Giveaways from "./pages/Giveaways";
import DealPage from "./pages/DealPage";
import DemoCollection from "./pages/DemoCollection";
import Partners from "./pages/Partners";
import CollectionView from "./pages/CollectionView";
import ContributorProfile from "./pages/ContributorProfile";
import CreateLook from "./pages/CreateLook";
import ContributorStudio from "./pages/ContributorStudio";
import PartnerDesk from "./pages/PartnerDesk";
import { About, Contact, Privacy, Terms, AffiliateDisclosurePage, NotFound } from "./pages/StaticPages";
import { Logo } from "./components/Logo";
import { DealLinkProvider } from "./context/DealLinkContext";
import { LovedDealsProvider } from "./context/LovedDealsContext";
import { HIDE_SALES } from "./lib/siteConfig";

function BrandLoader() {
  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4" data-testid="brand-loader">
      <Logo height="h-12" withTagline taglineClassName="text-[11px] mt-1.5 tracking-[0.18em]" className="animate-pulse" />
      <div className="h-1 w-24 rounded-full bg-brand/20 overflow-hidden">
        <div className="h-full w-1/2 bg-brand animate-[loadbar_1s_ease-in-out_infinite]" />
      </div>
      <style>{`@keyframes loadbar{0%{transform:translateX(-100%)}100%{transform:translateX(200%)}}`}</style>
    </div>
  );
}

function Protected({ children, admin, affiliate }) {
  const { user } = useAuth();
  if (user === null) return <BrandLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  if (affiliate && !(user.affiliate || user.role === "admin")) {
    return (
      <div data-testid="partners-only-denial" className="max-w-lg mx-auto mt-16 bg-white rounded-3xl border border-line shadow-soft p-10 text-center">
        <h1 className="font-heading font-black text-2xl text-ink">Approved partners only</h1>
        <p className="text-ink-soft mt-2">Deal Statistics and Post a Deal are available only to approved SIBLINGO partners. If you'd like to become a partner, please contact us.</p>
        <a href="/family-deals" className="inline-block mt-6 h-12 px-6 leading-[3rem] rounded-full bg-brand text-white font-bold hover:bg-brand-hover transition-all">Back to Family Deals</a>
      </div>
    );
  }
  return children;
}

function Shell({ children }) {
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SiteProvider>
          <BrowserRouter>
          <DealLinkProvider>
          <LovedDealsProvider>
            <Toaster position="top-center" richColors />
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/email/unsubscribe" element={<Shell><EmailUnsubscribe /></Shell>} />
              <Route path="/" element={<Shell><Home /></Shell>} />
              <Route path="/sales" element={HIDE_SALES ? <Navigate to="/family-deals" replace /> : <Shell><Sales /></Shell>} />
              <Route path="/giveaways" element={HIDE_SALES ? <Navigate to="/family-deals" replace /> : <Shell><Giveaways /></Shell>} />
              <Route path="/deals" element={<Shell><LatestDeals /></Shell>} />
              <Route path="/deal/:id" element={<Shell><DealPage /></Shell>} />
              <Route path="/marketplace" element={HIDE_SALES ? <Navigate to="/family-deals" replace /> : <Shell><Marketplace /></Shell>} />
              <Route path="/family-deals" element={<Shell><FamilyDeals /></Shell>} />
              <Route path="/about" element={<Shell><About /></Shell>} />
              <Route path="/contact" element={<Shell><Contact /></Shell>} />
              <Route path="/privacy" element={<Shell><Privacy /></Shell>} />
              <Route path="/terms" element={<Shell><Terms /></Shell>} />
              <Route path="/affiliate-disclosure" element={<Shell><AffiliateDisclosurePage /></Shell>} />
              <Route path="/partners" element={<Shell><Partners /></Shell>} />
              <Route path="/demo-collection" element={<Shell><DemoCollection /></Shell>} />
              <Route path="/c/:id" element={<Shell><CollectionView /></Shell>} />
              <Route path="/u/:handle" element={<Shell><ContributorProfile /></Shell>} />
              <Route path="/contribute" element={<Shell><CreateLook /></Shell>} />
              <Route path="/studio" element={<Shell><Protected><ContributorStudio /></Protected></Shell>} />
              <Route path="/partner-desk" element={<Shell><Protected><PartnerDesk /></Protected></Shell>} />
              <Route path="/family-profile" element={<Shell><Protected><FamilyProfile /></Protected></Shell>} />
              <Route path="/notify-me" element={<Shell><Protected><Alerts /></Protected></Shell>} />
              <Route path="/loved" element={<Shell><Protected><LovedDeals /></Protected></Shell>} />
              <Route path="/post-deal" element={<Shell><Protected><PostDeal /></Protected></Shell>} />
              <Route path="/merchant-stats" element={<Shell><Protected affiliate><MerchantStats /></Protected></Shell>} />
              <Route path="/alerts" element={<Navigate to="/notify-me" replace />} />
              <Route path="/me" element={<Shell><Protected><MyUptoo /></Protected></Shell>} />
              <Route path="/upgrade" element={<Shell><Protected><Upgrade /></Protected></Shell>} />
              <Route path="/payment/success" element={<Shell><PaymentSuccess /></Shell>} />
              <Route path="/payment/cancel" element={<Shell><PaymentCancel /></Shell>} />
              <Route path="/admin" element={<Shell><Protected admin><Admin /></Protected></Shell>} />
              <Route path="/admin/review/:pid" element={<Shell><Protected admin><ReviewDeal /></Protected></Shell>} />
              <Route path="*" element={<Shell><NotFound /></Shell>} />
            </Routes>
          </LovedDealsProvider>
          </DealLinkProvider>
          </BrowserRouter>
        </SiteProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
